import React, { FC, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Grid, TextField, Select, MenuItem, Autocomplete } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../store';
import useStyles from './styles';
import {
  deviceRebootAction,
  meterValueAdjustmentConfigAction,
  pingCommandsAction,
  queueAssetDeviceConfigurationAction,
  shortCommandAction,
  shortMessageRequestConfigAction,
  saberRekeyAction,
  transferOverrideConfigAction,
  factoryResetAction,
  queueCommands,
  handleSendFileToDevice,
  geoFenceDeleteConfigAction,
} from '../../../store/commands/general-commands/actions';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import { RebootRequestBody } from '../../../entities/reboot-commands-v2/models';
import {
  AssetDeviceConfigRequest,
  MachineStartMode,
  TamperResistance,
} from '../../../entities/asset-device-config-v2/models';
import { PingRequest } from '../../../entities/ping-commands-v2/models';
import { ShortMessagePostRequest } from '../../../entities/short-message-request-configuration-v1/models';
import { TransferOverridePostRequest } from '../../../entities/transfer-override-configurations-v1/models';
import { FileTypeId, SaberRekeyPayload } from '../../../entities/saber-rekey-v1/models';
import { MeterValueAdjustmentRequest } from '../../../entities/meter-value-adjustment-config/models';
import { GeoFenceRemoveRequest } from '../../../entities/geofenceConfigurations-v1/geoFence-delete-request';
import { postCommandsByDeviceKeyAction } from '../../../store/commands/actions';
import { setModalOpen } from '../../../store/modal/actions';
import { transferOverrideConfirmation } from '../messages';
import ResponsiveContainer from './responsive-command-container';
import { testIsPL240 } from '../../../globals/utils';
import FileUploader from '../../file-loader';
import { ConfigType } from '../../../entities/geofenceConfigurations-v1/config-type';

type SelectOption = {
  value: string | number;
  title: string;
};

type AutoSelectOption = {
  id: string;
  label: string;
};

const GeneralCommands: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const newSaberKeyOptions: SelectOption[] = [
    { title: 'Protocol Version 1', value: '10169' },
    { title: 'Protocol Version 2', value: '10200' },
  ];

  const tamperResistanceOptions: SelectOption[] = [
    { title: 'Default (Off)', value: 'off' },
    { title: 'Level_1', value: 'level_1' },
    { title: 'Level_2', value: 'level_2' },
    { title: 'Level_3', value: 'level_3' },
  ];

  const machineStartModeOptions: SelectOption[] = [
    { title: 'Normal', value: 'normal' },
    { title: 'Derate', value: 'derate' },
    { title: 'Disable', value: 'disable' },
  ];

  const fenceTypeOptions: SelectOption[] = [
    { title: 'GeoFence', value: 'geoFence' },
    { title: 'SpeedFence', value: 'speedFence' },
  ];

  const [shortMessageOptions, setShortMessageOptions] = useState<AutoSelectOption[] | []>([]);
  const [transferOverrideOptions, setTransferOverrideOptions] = useState<SelectOption[] | []>([]);
  const [dataFileOptions, setDataFileOptions] = useState<AutoSelectOption[] | []>([]);
  const [fileTypeId, setFileTypeId] = useState<string>('');
  const [fileTypeIdSendFileDevice, setFileTypeIdSendFileDevice] = useState<string>('');
  const [fenceId, setFenceId] = useState<string>('');
  const [data, setData] = useState<string>('');
  const [hours, setHours] = useState<string>('');
  const [confirmHours, setConfirmHours] = useState<string>('');
  const [hoursOdometer, setHoursOdometer] = useState<string>('');
  const [confirmHoursOdometer, setConfirmHoursOdometer] = useState<string>('');
  const [shortMessageValue, setShortMessageValue] = useState<string>('');
  const [transferOverrideValue, setTransferOverrideValue] = useState<string>('');
  const [tamperResistanceValue, setTamperResistanceValue] = useState<string>('off');
  const [machineStartModeValue, setMachineStartModeValue] = useState<MachineStartMode>('normal');
  const [dataFileValue, setDataFileValue] = useState<string>('');
  const [newSaberValue, setNewSaberValue] = useState<string>('');
  const [pushFileName, setPushFileName] = useState<string>('');
  const [fileToPush, setFileToPush] = useState<any>();
  const [deviceHex, setDeviceHex] = useState<string>('');
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const isPL240Device = testIsPL240(selectedEntity?.deviceType);
  const deviceType = useSelector((state: AppState) => state.search.selectedEntity?.deviceType);
  const { client } = useSelector((state: AppState) => state.login);
  const fileTypeConfigs = useSelector((state: AppState) => state.commands.fileTypeConfigs);
  const clientAuth: any = client?.authenticate();
  const userName = clientAuth.loginId;
  const deviceKey = selectedEntity?.deviceKey;
  const [fenceType, setFenceType] = useState<ConfigType>(ConfigType.GeoFence);
  const validFileType = /[^\d A-Za-z]/.test(fileTypeId);
  const validFileTypeSendFileDevice = /[^\d A-Za-z]/.test(fileTypeIdSendFileDevice);
  const validHexMessage = deviceHex.match(/[\da-f]$/gi);
  // eslint-disable-next-line unicorn/prefer-set-has
  const exclusiveDataFile: Array<string> = ["10001", "10004", "10330"];

  const is131or141 = () =>
    selectedEntity?.commercialType === 'PL141' || selectedEntity?.commercialType === 'PL131';

  const clearInputValues = () => {
    setFileTypeId('');
    setFileTypeIdSendFileDevice('');
    setPushFileName('');
    setData('');
    setHours('');
    setConfirmHours('');
    setHoursOdometer('');
    setConfirmHoursOdometer('');
    setTransferOverrideValue('');
    setTamperResistanceValue('');
    setMachineStartModeValue('normal');
    setNewSaberValue('');
    setFenceId('');
    setFenceType(ConfigType.GeoFence);
  };

  useEffect(() => {
    clearInputValues();
  }, [selectedEntity]);
  const openModel = (message: JSX.Element | string, callback: Function) => {
    dispatch(
      setModalOpen({
        open: true,
        message,
        accept: callback,
        cancel: () => { },
      }),
    );
  };

  const onClickOfDeviceReboot = async () => {
    if (selectedEntity?.deviceKey) {
      const input: RebootRequestBody = {
        configs: [
          {
            deviceKey: selectedEntity.deviceKey,
          },
        ],
        userId: userName,
      };
      dispatch(deviceRebootAction(input, deviceKey));
      clearInputValues();
    }
  };

  const onClickOfAssetUpdate = async () => {
    if (deviceKey) {
      const input: PingRequest = {
        configs: [{ deviceKey }],
        userId: userName,
      };
      dispatch(pingCommandsAction(input, deviceKey, deviceType));
      clearInputValues();
    }
  };

  const onClickOfShortMessageOnDemand = async (fileTypeIdInput: string) => {
    if (fileTypeIdInput === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to select file type for Short Message Command',
          options: { variant: 'error' },
        }),
      );
    } else if (deviceKey) {
      const input: ShortMessagePostRequest = {
        configs: [{ deviceKey, fileTypeId: fileTypeIdInput }],
        userId: userName,
      };
      const classificationType = { classification: 'PLMessage' };
      dispatch(shortMessageRequestConfigAction(input, deviceKey, classificationType));
      clearInputValues();
    }
  };

  const onClickOfSendCommand = async () => {
    if (selectedEntity && deviceKey) {
      if (fileTypeId !== '10565' && (!data || !fileTypeId)) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Need to enter File Type id and Data',
            options: { variant: 'error' },
          }),
        );
      } else {
        const input = {
          configs: [
            {
              deviceKey,
              fileTypeId,
              data,
            },
          ],
          wakeup: false,
          wakeupType: 'all',
          reboot: false,
          userId: userName,
          applicationId: 'iot-gateway',
        };
        if (isPL240Device) {
          const inputValues = {
            deviceKey,
            deviceData: [
              {
                fileType: Number.parseInt(fileTypeId, 10),
                deviceKey,
                data,
              },
            ],
          };
          dispatch(postCommandsByDeviceKeyAction(inputValues));
          clearInputValues();
        } else {
          dispatch(shortCommandAction(input, deviceKey));
          clearInputValues();
        }
      }
    }
  };

  const onClickOfTamperResistance = async () => {
    if (tamperResistanceValue === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to select Tamper Resistance option',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity && deviceKey) {
      const input: AssetDeviceConfigRequest = {
        configs: [
          {
            deviceKey,
            disableDerate: {
              disableDerateStartMode: {
                machineStartMode: machineStartModeValue,
              },
              disableDerateTamperResistance: {
                tamperResistance: tamperResistanceValue as TamperResistance,
              },
            },
          },
        ],
        userId: userName,
        wakeup: false,
        wakeupType: 'all',
        reboot: false,
      };
      dispatch(queueAssetDeviceConfigurationAction(input, deviceKey));
      clearInputValues();
    }
  };

  const getShortMessageOptions = useCallback(async () => {
    const selectOptions = fileTypeConfigs
      ? fileTypeConfigs
        .filter(
          (f) => f.fileTypeName && f.classification === 'PLMessage' && f.isOnDemandSupported,
        )
        .sort((a, b) => {
          const fileTypeNameA = a.fileTypeName?.toUpperCase();
          const fileTypeNameB = b.fileTypeName?.toUpperCase();
          if (fileTypeNameA && fileTypeNameB) {
            if (fileTypeNameA < fileTypeNameB) return -1;
            if (fileTypeNameA > fileTypeNameB) return 1;
          }
          return 0;
        })
        .map((f) => ({
          id: f.fileTypeId,
          label: `${f.fileTypeName ? `${f.fileTypeId} - ${f.fileTypeName}` : ''}`,
        }))
      : [];
    setShortMessageOptions(selectOptions);
  }, [fileTypeConfigs]);

  const getDataFileOptions = useCallback(async () => {
    const selectOptions = fileTypeConfigs
      ? fileTypeConfigs
        .filter((f) => f.classification === 'DATA_FILE' || f.classification === 'dataFile' || exclusiveDataFile.includes(f.fileTypeId))
        .sort((a, b) => {
          const fileTypeNameA = a.fileTypeName?.toUpperCase();
          const fileTypeNameB = b.fileTypeName?.toUpperCase();
          if (fileTypeNameA && fileTypeNameB) {
            if (fileTypeNameA < fileTypeNameB) return -1;
            if (fileTypeNameA > fileTypeNameB) return 1;
          }
          return 0;
        })
        .map((f) => ({
          id: f.fileTypeId,
          label: `${f.fileTypeId} - ${f.fileTypeName}`,
        }))
      : [];
    setDataFileOptions(selectOptions);
  }, [fileTypeConfigs]);

  const getTransferOverrideOptions = useCallback(async () => {
    const selectOptions = fileTypeConfigs
      ? fileTypeConfigs
        .filter((f) => f.isTransferOverrideEnabled)
        .sort((a, b) => {
          const fileTypeNameA = a.fileTypeName?.toUpperCase();
          const fileTypeNameB = b.fileTypeName?.toUpperCase();
          if (fileTypeNameA && fileTypeNameB) {
            if (fileTypeNameA < fileTypeNameB) return -1;
            if (fileTypeNameA > fileTypeNameB) return 1;
          }
          return 0;
        })
        .map((f) => ({
          value: f.fileTypeId,
          title: `${f.fileTypeId} - ${f.fileTypeName}`,
        }))
      : [];
    setTransferOverrideOptions(selectOptions);
  }, [fileTypeConfigs]);

  const onClickOfOnDemand = async (fileTypeIdInput: string) => {
    if (fileTypeIdInput === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to select file type for Date file on Demand',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity?.deviceId && selectedEntity?.deviceType) {
      const input: ShortMessagePostRequest = {
        configs: [
          {
            deviceId: selectedEntity.deviceId,
            deviceType: selectedEntity.deviceType,
            fileTypeId: fileTypeIdInput,
          },
        ],
        userId: userName,
      };
      const classificationType = { classification: 'datafile' };
      dispatch(shortMessageRequestConfigAction(input, deviceKey, classificationType));
      clearInputValues();
    }
  };

  const onClickOfNewSaberKey = async () => {
    if (newSaberValue === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to select option for Saber Key to Device command',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity?.deviceId && selectedEntity?.deviceType && deviceKey) {
      const input: SaberRekeyPayload = {
        deviceId: selectedEntity.deviceId,
        deviceKey,
        deviceType: selectedEntity.deviceType,
        fileTypeId: newSaberValue as FileTypeId,
        userId: userName,
      };
      dispatch(saberRekeyAction(input, deviceKey));
      clearInputValues();
    }
  };

  const onClickOfTransferOverride = async () => {
    if (transferOverrideValue === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to select file type for Transfer Override command',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity && deviceKey) {
      openModel(transferOverrideConfirmation, () => {
        const input: TransferOverridePostRequest = {
          configs: [
            {
              deviceKey,
              fileTypeId: transferOverrideValue,
            },
          ],
          wakeup: false,
          reboot: false,
          userId: userName,
        };
        dispatch(transferOverrideConfigAction(input, deviceKey));
        clearInputValues();
      });
    }
  };

  const onClickOfSetMeterHours = async () => {
    if (hours !== confirmHours) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Command values entered for Set Service Meter Hours do not match',
          options: { variant: 'error' },
        }),
      );
    } else if (!hours || !confirmHours) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to add values for Service Meter Hours',
          options: { variant: 'error' },
        }),
      );
    } else if (hours.includes('.') && hours.slice(Math.max(0, hours.indexOf('.') + 1)).length > 1) {
      dispatch(
        enqueueSnackbarAction({
          message:
            'Command values for Set Service Meter Hours does not support more than one decimal',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity && deviceKey) {
      const input: MeterValueAdjustmentRequest = {
        configs: [
          {
            deviceKey,
            serviceMeterHourAdjustment: {
              hoursAdjusted: Number.parseFloat(hours),
            },
          },
        ],
      };
      dispatch(meterValueAdjustmentConfigAction(input, deviceKey));
      clearInputValues();
    }
  };

  const onClickOfSetConfirmHoursOdometerValue = async () => {
    if (hoursOdometer !== confirmHoursOdometer) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Command values entered for Set Odometer do not match',
          options: { variant: 'error' },
        }),
      );
    } else if (!hoursOdometer || !confirmHoursOdometer) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Need to add values for Odometer',
          options: { variant: 'error' },
        }),
      );
    } else if (hoursOdometer.includes('.') && hoursOdometer.slice(Math.max(0, hoursOdometer.indexOf('.') + 1)).length > 1) {
      dispatch(
        enqueueSnackbarAction({
          message:
            'Command values for Set Odometer does not support more than one decimal',
          options: { variant: 'error' },
        }),
      );
    } else if (selectedEntity && deviceKey) {
      const input: MeterValueAdjustmentRequest = {
        configs: [
          {
            deviceKey,
            serviceOdometerAdjustment: {
              odometerAdjusted: Number.parseFloat(hoursOdometer),
            },
          },
        ],
      };
      dispatch(meterValueAdjustmentConfigAction(input, deviceKey));
      clearInputValues();
    }
  };

  const onClickOfDeleteGeoFence = async () => {
    if (selectedEntity && deviceKey) {
      const input: GeoFenceRemoveRequest = {
        configs: [
          {
            deviceKey,
            configType: fenceType,
            fenceIds: fenceId ? [fenceId] : undefined,
          }]
      };
      dispatch(geoFenceDeleteConfigAction(input, deviceKey));
      clearInputValues();
    }
  };


  const onClickGenericCommand = async () => {
    if (selectedEntity) {
      const success: any = await dispatch(
        queueCommands({
          deviceId: selectedEntity.deviceId,
          deviceType: selectedEntity.deviceType,
          data: deviceHex,
        }),
      );
      if (success !== null) {
        setDeviceHex('');
      }
    }
  };
  const onClickFactoryReset = async () => {
    if (selectedEntity) {
      await dispatch(
        factoryResetAction({
          deviceId: selectedEntity.deviceId,
          deviceType: selectedEntity.deviceType,
        }),
      );
    }
  };

  const onClickOfSendFileToDevice = async () => {
    if (fileToPush === '' || pushFileName === '') {
      dispatch(
        enqueueSnackbarAction({
          message: 'Please select File to Push!',
          options: { variant: 'error' },
        }),
      );
    } else {
      dispatch(handleSendFileToDevice(fileToPush, fileTypeIdSendFileDevice));
      clearInputValues();
    }
  };

  const fileHandler = (file: any) => {
    if (file && file.name.endsWith('.docx')) {
      setPushFileName('');
      dispatch(
        enqueueSnackbarAction({
          message: 'Document Files are not allowed!',
          options: { variant: 'error' },
        }),
      );
    } else {
      setPushFileName(file.name);
      setFileToPush(file);
    }
  };

  useEffect(() => {
    getShortMessageOptions();
    getDataFileOptions();
    getTransferOverrideOptions();
  }, [fileTypeConfigs, getShortMessageOptions, getDataFileOptions, getTransferOverrideOptions]);

  if (is131or141()) {
    return (
      <Grid container direction="row">
        <ResponsiveContainer
          title="Factory Reset"
          labelClass={classes.deviceSpecificLabel}
          buttonClass={classes.deviceSpecificButton}
          button={{
            id: 'device',
            doubleCheck: true,
            clickCallBack: onClickFactoryReset,
          }}
          dropdowns={
            <></>
          }
        />
        <ResponsiveContainer
          title="Send Generic Command to Device (HEX)"
          labelClass={classes.deviceSpecificLabel}
          buttonClass={classes.deviceSpecificButton}
          disableSubmit={!validHexMessage}
          button={{
            id: 'device',
            doubleCheck: true,
            clickCallBack: onClickGenericCommand,
          }}
          dropdowns={
            <>
              <TextField
                aria-label="Hex"
                placeholder="Hex Data"
                variant="standard"
                style={{ width: '100%' }}
                value={deviceHex}
                onChange={(event) => setDeviceHex(event.target.value)}
              />
            </>
          }
        />
      </Grid>
    );
  }

  return (
    <Grid container direction="row">
      <ResponsiveContainer
        title="Device Reboot"
        button={{
          id: 'device',
          clickCallBack: onClickOfDeviceReboot,
          doubleCheck: true,
        }}
      />
      {!isPL240Device && (
        <ResponsiveContainer
          title="Asset Update on Demand"
          button={{
            id: 'assetUpdateOnDemand',
            clickCallBack: onClickOfAssetUpdate,
            doubleCheck: true,
          }}
        />
      )}
      <ResponsiveContainer
        title="Send Command to Device"
        dropdowns={
          <>
            <Box px={1}>
              <TextField
                variant="standard"
                error={validFileType}
                id="fileTypeTextField"
                label="File Type #"
                value={fileTypeId}
                type="text"
                onChange={(event) => {
                  setFileTypeId(event.target.value);
                }}
              />
            </Box>
            <Box px={1}>
              <TextField
                variant="standard"
                id="dataTextField"
                label="Data"
                value={data}
                onChange={(event) => {
                  setData(event.target.value);
                }}
              />
            </Box>
          </>
        }
        button={{
          id: 'sendCommandAddToQueueButton',
          clickCallBack: onClickOfSendCommand,
          doubleCheck: true,
        }}
      />
      {!isPL240Device && (
        <>
          <ResponsiveContainer
            title="Short Message on Demand"
            dropdowns={
              <Autocomplete
                id="shortMessageSelect"
                options={shortMessageOptions}
                className={classes.select}
                renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setShortMessageValue(newValue.id as string);
                  }
                }}
              />
            }
            button={{
              id: 'shortMessageOnDemandButton',
              clickCallBack: () => onClickOfShortMessageOnDemand(shortMessageValue),
              doubleCheck: true,
            }}
          />
          <ResponsiveContainer
            title="Data file on Demand"
            dropdowns={
              <Autocomplete
                id="dataFileOnDemandSelect"
                options={dataFileOptions}
                className={classes.select}
                renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setDataFileValue(newValue.id as string);
                  }
                }}
              />
            }
            button={{
              id: 'dataFileOnDemandButton',
              clickCallBack: () => onClickOfOnDemand(dataFileValue),
              doubleCheck: true,
            }}
          />
          <ResponsiveContainer
            title="Send New Saber Key to Device"
            dropdowns={
              <Select
                variant="standard"
                id="sendNewSaberKeySelect"
                value={newSaberValue || 'select'}
                onChange={(event) => setNewSaberValue(event.target.value as FileTypeId)}
                className={classes.select}
              >
                <MenuItem value="select">
                  <em className={classes.unSelected}>Select</em>
                </MenuItem>
                {(newSaberKeyOptions as Array<SelectOption>).map((o: SelectOption) => (
                  <MenuItem value={o.value}>{o.title}</MenuItem>
                ))}
              </Select>
            }
            button={{
              id: 'sendNewSaberKeyButton',
              clickCallBack: () => onClickOfNewSaberKey(),
              doubleCheck: true,
            }}
          />
          <ResponsiveContainer
            title="Transfer Override"
            dropdowns={
              <Select
                variant="standard"
                id="transferOverrideSelect"
                value={transferOverrideValue || 'select'}
                onChange={(event) => setTransferOverrideValue(event.target.value as string)}
                className={classes.select}
              >
                <MenuItem value="select">
                  <em className={classes.unSelected}>Select</em>
                </MenuItem>
                {(transferOverrideOptions as Array<SelectOption>).map((o: SelectOption) => (
                  <MenuItem value={o.value}>{o.title}</MenuItem>
                ))}
              </Select>
            }
            button={{
              id: 'transferOverrideButton',
              clickCallBack: () => onClickOfTransferOverride(),
            }}
          />
        </>
      )}
      <ResponsiveContainer
        title="Set Service Meter Hours"
        dropdowns={
          <Box width="100%" display="flex" justifyContent="space-between">
            <TextField
              variant="standard"
              type="number"
              id="hoursTextField"
              onChange={(event) => {
                setHours(event.target.value);
              }}
              value={hours}
              label="Hours"
            />
            <Box px={2} display="flex" alignItems="flex-end">
              <Typography variant="h6">Confirm</Typography>
            </Box>
            <TextField
              variant="standard"
              type="number"
              id="confirmHoursTextField"
              onChange={(event) => {
                setConfirmHours(event.target.value);
              }}
              value={confirmHours}
              label="Hours"
            />
          </Box>
        }
        button={{
          id: 'setHoursAddToQueueButton',
          clickCallBack: () => onClickOfSetMeterHours(),
          doubleCheck: true,
        }}
      />
      {!isPL240Device && (
        <ResponsiveContainer
          title="Tamper Resistance"
          dropdowns={
            <Box width="100%" display="flex" justifyContent="space-between">
              <Select
                variant="standard"
                id="tamperResistanceSelect"
                value={tamperResistanceValue || 'select'}
                onChange={(event) => setTamperResistanceValue(event.target.value as string)}
                className={classes.select}
              >
                <MenuItem value="select">
                  <em className={classes.unSelected}>Select</em>
                </MenuItem>
                {(tamperResistanceOptions as Array<SelectOption>).map((o: SelectOption) => (
                  <MenuItem value={o.value}>{o.title}</MenuItem>
                ))}
              </Select>
              <Box px={2} display="flex" alignItems="flex-end">
                <Typography variant="h6">Start Mode</Typography>
              </Box>
              <Select
                variant="standard"
                id="machineStartMode"
                value={machineStartModeValue || 'select'}
                onChange={(event) =>
                  setMachineStartModeValue(event.target.value as MachineStartMode)
                }
                className={classes.select}
              >
                <MenuItem value="select">
                  <em className={classes.unSelected}>Select</em>
                </MenuItem>
                {(machineStartModeOptions as Array<SelectOption>).map((o: SelectOption) => (
                  <MenuItem value={o.value}>{o.title}</MenuItem>
                ))}
              </Select>
            </Box>
          }
          button={{
            id: 'tamperResistanceButton',
            clickCallBack: () => onClickOfTamperResistance(),
            doubleCheck: true,
          }}
        />
      )}
      <ResponsiveContainer
        title="Set Odometer Value"
        dropdowns={
          <Box width="100%" display="flex" justifyContent="space-between">
            <TextField
              variant="standard"
              type="number"
              id="hoursOdometerTextField"
              onChange={(event) => {
                setHoursOdometer(event.target.value);
              }}
              value={hoursOdometer}
              label="Value"
            />
            <Box px={2} display="flex" alignItems="flex-end">
              <Typography variant="h6">Confirm</Typography>
            </Box>
            <TextField
              variant="standard"
              type="number"
              id="confirmHoursOdometerTextField"
              onChange={(event) => {
                setConfirmHoursOdometer(event.target.value);
              }}
              value={confirmHoursOdometer}
              label="Value"
            />
          </Box>
        }
        button={{
          id: 'setHoursOdometerAddToQueueButton',
          clickCallBack: () => onClickOfSetConfirmHoursOdometerValue(),
          doubleCheck: true,
        }}
      />
      <ResponsiveContainer
        title="Send File to Device"
        dropdowns={
          <Box width="100%" display="flex" justifyContent="space-between">
            <TextField
              variant="standard"
              error={validFileTypeSendFileDevice}
              id="fileTypeTextField"
              label="File Type #"
              value={fileTypeIdSendFileDevice}
              type="text"
              onChange={(event) => {
                setFileTypeIdSendFileDevice(event.target.value);
              }}
            />
            <Box display="flex" alignItems="flex-end">
              <FileUploader handleFile={fileHandler} accept='.pb' />
            </Box>
            <Box display="flex" alignItems="flex-end">
              <TextField
                variant="standard"
                type="text"
                value={pushFileName}
                label=""
              />
            </Box>
          </Box>
        }
        button={{
          id: 'setHoursAddToQueueButton',
          clickCallBack: () => onClickOfSendFileToDevice(),
          doubleCheck: true,
        }}
      />

      <ResponsiveContainer
        title="Delete GeoFence"
        dropdowns={
          <>
            <Select
              variant="standard"
              id="geoFenceConfigType"
              value={fenceType || 'select'}
              onChange={(event) =>
                setFenceType(event.target.value as ConfigType)
              }
              className={classes.select}
            >
              <MenuItem value="Select">
                <em className={classes.unSelected}>Fence Type</em>
              </MenuItem>
              {(fenceTypeOptions as Array<SelectOption>).map((o: SelectOption) => (
                <MenuItem value={o.value}>{o.title}</MenuItem>
              ))}
            </Select>

            <Box px={1}>
              <TextField
                variant="standard"
                id="fenceIdTextField"
                label="Offboard Fence Id"
                value={fenceId}
                type="text"
                onChange={(event) => {
                  setFenceId(event.target.value);
                }}
              />
            </Box>
          </>
        }
        button={{
          id: 'deleteGeoFenceButton',
          clickCallBack: onClickOfDeleteGeoFence,
          doubleCheck: true,
        }}
      />

    </Grid>
  );
};

export default GeneralCommands;
